import { useState } from "react";
import { Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import ConvertToString from "../../../components/Converter";
import PlusIcon from "../../../components/Icons/PlusIcon";
import Dash from "../../../components/Math/Units/Metric/Dash";
import Metre from "../../../components/Math/Units/Metric/Metre";
import Millimetre from "../../../components/Math/Units/Metric/Millimetre";
import NewtonDividedByMillimetreSquared from "../../../components/Math/Units/Metric/NewtonDividedByMillimetreSquared";
import { RowBody } from "../../../components/RowValue/RowBody";
import { ResultSpan } from "../../../components/styledComponents/Result";
import ToolTip, { IconType } from "../../../components/ToolTip";
import HiddenWrapperRow from "../../../components/UI/HiddenWrapperRow";
import { useGetTraggliedDatenQuery } from "../../../services/API/titanApi";
import {
  selectDruckfestigkeitZement,
  selectTeilsicherheitsbeiwertTransmission,
  selectVerbundbedingungenEta1,
  setVerbundbedingungenEta1,
} from "../../../store/slices/aeussereSlice";
import {
  selectBondStrength,
  selectIsTransmissionLengthValid,
  selectUebertragungslaenge,
} from "../../../store/slices/selectors/Transmission";
import { selectTragglied } from "../../../store/slices/traggliedSlice";
import { f_bd_Formula, l_b_Formula } from "../TeXFormula";
import BondConditions from "./LoadTransmissionLength/BondConditions";
import BondConditionsDetails from "./LoadTransmissionLength/BondConditionsDetails";
import CompressiveStrengthOfConcrete from "./LoadTransmissionLength/CompressiveStrengthOfConcrete";
import CompressiveStrengthOfConcreteDetails from "./LoadTransmissionLength/CompressiveStrengthOfConcreteDetails";

export default function Transmission() {
  const { t } = useTranslation("translation");
  const dispatch = useDispatch();
  // #region Redux Store Variables
  const tragglied = useSelector(selectTragglied);
  const { data: traggliedDaten } = useGetTraggliedDatenQuery(tragglied.title);
  const teilsicherheitsbeiwertTransmission = useSelector(selectTeilsicherheitsbeiwertTransmission);
  const verbundbedingungenEta1 = useSelector(selectVerbundbedingungenEta1);
  const druckfestigkeitZement = useSelector(selectDruckfestigkeitZement);
  const uebertragungslaenge = useSelector(selectUebertragungslaenge(traggliedDaten));
  const bondStrength = useSelector(selectBondStrength(traggliedDaten));
  const isValid = useSelector(selectIsTransmissionLengthValid(traggliedDaten));
  // #endregion

  const [showBondConditionsDetails, setShowBondConditionsDetails] = useState(false);
  const [showCompressiveStrengthOfConcreteDetails, setShowCompressiveStrengthOfConcreteDetails] =
    useState(false);

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <h3>{t("aeussre.loadTransmissionLength")}</h3>
        </div>
      </div>
      <div className="row">
        <CompressiveStrengthOfConcrete />

        <HiddenWrapperRow>
          <CompressiveStrengthOfConcreteDetails
            show={showCompressiveStrengthOfConcreteDetails}
            setShow={setShowCompressiveStrengthOfConcreteDetails}
          />
        </HiddenWrapperRow>

        <RowBody
          titleValue={<p>{t("aeussre.concreteTensileStrength")}</p>}
          indicesValue={
            <span>
              f<sub>ctk,0.05</sub>
            </span>
          }
          inputValue={<span className="input">{ConvertToString(druckfestigkeitZement)}</span>}
          unitValue={<NewtonDividedByMillimetreSquared />}
          formulaValue={
            <span onClick={() => setShowCompressiveStrengthOfConcreteDetails(true)}>
              <PlusIcon />
            </span>
          }
        />

        <RowBody
          titleValue={<p>{t("aeussre.partialSafetyFactor")}</p>}
          indicesValue={
            <span>
              &gamma;<sub>c</sub>
            </span>
          }
          inputValue={
            <span className="input">{ConvertToString(teilsicherheitsbeiwertTransmission)}</span>
          }
        />
        <RowBody
          titleValue={<p>{t("aeussre.GoodBondConditions")}</p>}
          inputValue={
            <select
              className="change-input"
              name="GoodBondConditions"
              onChange={(e) => {
                dispatch(setVerbundbedingungenEta1(Number(e.target.value)));
              }}
              value={verbundbedingungenEta1}
            >
              <option value={1.0}>{t("aeussre.GoodBondConditions_well")}</option>
              <option value={0.7}>{t("aeussre.GoodBondConditions_moderate")}</option>
            </select>
          }
        />

        <RowBody
          //titleValue={<p>{t("aeussre.GoodBondConditions")}</p>}
          indicesValue={
            <span>
              &eta;<sub>1</sub>
            </span>
          }
          inputValue={<span className="input">{ConvertToString(verbundbedingungenEta1, 1)}</span>}
        />

        <BondConditions
          showModal={setShowBondConditionsDetails}
          value={traggliedDaten?.beiwert_eta2}
        />
        <Row>
          <HiddenWrapperRow>
            <BondConditionsDetails
              show={showBondConditionsDetails}
              setShow={setShowBondConditionsDetails}
            />
          </HiddenWrapperRow>
        </Row>

        <RowBody
          titleValue={<p>{t("aeussre.bondStrength")}</p>}
          indicesValue={
            <span>
              f<sub>bd</sub>
            </span>
          }
          inputValue={<span className="input">{ConvertToString(bondStrength, 2)}</span>}
          unitValue={<NewtonDividedByMillimetreSquared />}
          formulaValue={
            <ToolTip id={"f_bd_Formula"} type={IconType.FORMULA} formula={f_bd_Formula} />
          }
        />
        <RowBody
          titleValue={<p>{t("aeussre.outerDiameter")}</p>}
          indicesValue={
            <span>
              D<sub>a</sub>
            </span>
          }
          inputValue={<span className="input">{ConvertToString(traggliedDaten?.durchmesser)}</span>}
          unitValue={<Millimetre />}
        />

        <RowBody
          titleValue={<p>{t("aeussre.transmissionLength")}</p>}
          indicesValue={
            <span>
              l<sub>b</sub>
            </span>
          }
          inputValue={
            <ResultSpan valid={isValid}>{ConvertToString(uebertragungslaenge, 2)}</ResultSpan>
          }
          unitValue={<Metre />}
          formulaValue={
            <ToolTip id={"l_b_Formula"} type={IconType.FORMULA} formula={l_b_Formula} />
          }
        />
      </div>
    </>
  );
}
